// hubs.jsx
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './hubs.css';
import hubsAbout from '../../assets/hubs_about.png';
import Hub1logo from '../../assets/qkenya_logo.png';
import Hub2logo from '../../assets/QuantumAnchor_logo.png';
import Hub3logo from '../../assets/Qresearch_logo.png';
import Hub1banner from '../../assets/hub1banner.jpg';
import Hub3banner from '../../assets/Hub3banner.jpg';
import whitePaperBanner from '../../assets/whitePapeer_banner.jpg';


// Main App Component
const App = () => {
  const [currentView, setCurrentView] = useState('home');
  const [selectedHub, setSelectedHub] = useState(null);

  const hubs = [
    {
      id: 'education',
      name: 'QKenya',
      subtitle: 'Education Hub',
      color: '#D4AF37',
      bannerImage: Hub1banner,
      logo: Hub1logo,
      description: 'Welcome to the QKenya Education Hub — the Kenyan chapter of the global QWorld community dedicated to expanding quantum literacy and education across Kenya and beyond. As part of this international network, we work to make quantum science and technology accessible to all.',
      content: 'The QKenya Education Hub partners with QWorld — a global quantum network that popularizes quantum technologies and builds open-access learning pathways for learners and educators around the world — to provide inclusive and hands-on educational opportunities in quantum computing and related fields. We offer workshops, tutorials, collaborative study sessions, and certification-aligned training that bridge foundational concepts with practical skills grounded in quantum programming and applications. Through partnerships with universities, community groups, and online platforms, we strive to democratize quantum education, empower local talent, and contribute to building a vibrant African quantum ecosystem that aligns with broader global goals for equitable access to science and technology.'
    },
    {
      id: 'industry',
      name: 'Quantum Anchor',
      subtitle: 'Industry & Policy Application Hub',
      color: '#B83A3A',
      bannerImage: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1200&h=400&fit=crop',
      logo: Hub2logo,
      description: 'The Quantum Anchor Hub is a joint initiative between QuantumKE and Action Lab, operating as the Design & Delivery platform of the Office of the Special Envoy on Technology Kenya . The hub serves as an operational bridge for applying quantum science at the intersection of industry, policy, and emerging technologies.',
      content: 'Quantum Anchor brings together QuantumKE and Action Lab to coordinate capacity building, research, policy engagement, and high-impact project delivery at the convergence of quantum technologies, AI, and innovation. The hub focuses on translating quantum capabilities into real-world, SDG-aligned use cases across sectors such as healthcare, finance, cybersecurity, optimisation, and climate resilience. Through joint activities including talent development programs, ecosystem mapping, applied pilots, and thought leadership initiatives such as quantum white papers, the hub supports Kenya\'s quantum readiness while contributing to responsible, inclusive, and future-oriented technology adoption in line with Africa\'s Agenda 2063.'
    },
    {
      id: 'research',
      name: 'QResearch',
      subtitle: 'Research & Innovation Hub',
      color: '#2D7A3E',
      bannerImage: Hub3banner,
      logo: Hub3logo,
      description: 'The QResearch Hub unites African quantum research excellence by collaborating with university institutions that have dedicated quantum research facilities, advancing impactful research, innovation, and discovery in quantum science and technology.',
      content: 'Through strategic partnerships with university institutions that host dedicated quantum research facilities — centres of excellence driving frontier research in quantum computing, sensing, communication, and applied technologies — the QResearch Hub supports cutting-edge research, interdisciplinary projects, and collaborative knowledge exchange across Africa. We catalyse research in areas such as quantum algorithms, secure quantum communications, quantum imaging, and quantum-enabled devices, while fostering partnerships that bridge academia, industry, and global research networks. By facilitating access to shared infrastructure, joint project development, seminars, and research symposia, the QResearch Hub helps position Kenya and the continent as active contributors to the global quantum research ecosystem and works towards SDG-aligned innovation that addresses societal challenges and unlocks inclusive technological growth.'
    }
  ];

  const handleHubClick = (hub) => {
    setSelectedHub(hub);
    setCurrentView('detail');
    window.scrollTo(0, 0);
  };

  const handleBackToHome = () => {
    setCurrentView('home');
    setSelectedHub(null);
    window.scrollTo(0, 0);
  };

  if (currentView === 'detail' && selectedHub) {
    return <HubDetailPage hub={selectedHub} onBack={handleBackToHome} />;
  }

  return <HubsPage hubs={hubs} onHubClick={handleHubClick} />;
};

// Main Hubs Page Component
const HubsPage = ({ hubs, onHubClick }) => {
  return (
    <div className="qke-hubs-container">
      <Helmet>
        <title>Quantum Hubs | QuantumKE</title>
        <meta name="description" content="Explore QuantumKE's hubs — specialized spaces for quantum education, research, and entrepreneurship across Kenya and Africa." />
        <link rel="canonical" href="https://quantumke.org/hubs" />
      </Helmet>

      {/* ── Header ── */}
      <div className="qke-hubs-header">
        <div className="qke-hubs-header-grid" aria-hidden />
        <div className="qke-hubs-header-glow" aria-hidden />
        <div className="qke-hubs-header-inner">
          <div className="qke-hubs-header-text">
            <span className="qke-hubs-label">QuantumKE Hubs</span>
            <h1 className="qke-hubs-title">
              Where Quantum Work<br />
              <span className="qke-hubs-title-accent">Gets Done</span>
            </h1>
            <p className="qke-hubs-lead">
              Three focused hubs — Education, Industry & Policy, and Research — each formed through
              strategic partnerships that translate quantum science into real-world impact across Africa.
            </p>
          </div>
          <div className="qke-hubs-header-image">
            <img src={hubsAbout} alt="QuantumKE hubs" />
          </div>
        </div>
      </div>

      {/* ── Hub Cards ── */}
      <div className="qke-hubs-grid">
        {hubs.map((hub) => (
          <div
            key={hub.id}
            className="qke-hub-card"
            style={{ backgroundColor: hub.color }}
            onClick={() => onHubClick(hub)}
          >
            <div className="qke-hub-pattern" />
            <div className="qke-hub-icon">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                <circle cx="20" cy="20" r="18" stroke="white" strokeWidth="2"/>
                <path d="M15 25L25 15M25 15H15M25 15V25" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="qke-hub-content">
              <h2 className="qke-hub-name">{hub.name}</h2>
              <p className="qke-hub-subtitle">{hub.subtitle}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Share Use Case Modal Component
const ShareUseCaseModal = ({ hubColor, onClose }) => {
  const [form, setForm] = useState({ title: '', contributors: '', email: '', description: '', sdg: '' });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError(false);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: '11c0a4ab-ec86-416a-9a30-1201b66ac3cb',
          subject: `Use Case Submission: ${form.title}`,
          name: form.contributors,
          email: form.email,
          title: form.title,
          description: form.description,
          sdg: form.sdg || 'Not specified',
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSuccess(true);
        setForm({ title: '', contributors: '', email: '', description: '', sdg: '' });
        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 3000);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('Error submitting use case:', err);
      setError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="qke-modal-overlay" onClick={onClose}>
      <div className="qke-modal" onClick={(e) => e.stopPropagation()}>
        <div className="qke-modal-header" style={{ borderBottomColor: hubColor }}>
          <h3>Share Your Use Case</h3>
          <button className="qke-modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <form onSubmit={handleSubmit} className="qke-modal-form">
          <label>
            Title *
            <input name="title" value={form.title} onChange={handleChange} required placeholder="e.g. Quantum ML for Climate Modelling" />
          </label>
          <label>
            Contributors *
            <input name="contributors" value={form.contributors} onChange={handleChange} required placeholder="Names and affiliations" />
          </label>
          <label>
            Email *
            <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="your@email.com" />
          </label>
          <label>
            Description *
            <textarea name="description" value={form.description} onChange={handleChange} required rows={5} placeholder="Describe your use case, methodology, and expected impact..." />
          </label>
          <label>
            Relevant SDG
            <input name="sdg" value={form.sdg} onChange={handleChange} placeholder="e.g. SDG 3 – Good Health and Well-being" />
          </label>
          <button
            type="submit"
            className="qke-cta-button"
            style={{ backgroundColor: success ? '#2D7A3E' : error ? '#B83A3A' : hubColor }}
            disabled={sending || success}
          >
            {sending ? 'Submitting...' : success ? 'Use Case Submitted!' : error ? 'Error! Try again' : 'Submit Use Case'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Individual Hub Detail Page Component
const HubDetailPage = ({ hub, onBack }) => {
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const useCases = [
    {
      title: 'Quantum Machine Learning for Early Cancer Diagnosis',
      sdg: 'SDG 3',
      sdgTitle: 'GOOD HEALTH AND WELL-BEING',
      sdgColor: '#4C9F38',
      description: 'Applies quantum SVMs and quantum neural networks to medical imaging to improve detection and classification of early-stage tumours, aiming to deliver a reliable clinical diagnostic tool that enhances patient outcomes.',
      contributor: 'Contributors: Shannon Mujera, Nicabed Ngathaba'
    },
    {
      title: 'Quantum Portfolio Optimisation using QAOA',
      sdg: 'SDG 8',
      sdgTitle: 'DECENT WORK AND ECONOMIC GROWTH',
      sdgColor: '#A21942',
      description: 'Maps portfolio optimisation to a QUBO/Ising Hamiltonian and solves it with QAOA on Qiskit, selecting asset subsets that maximise returns and minimise risk and transaction costs — demonstrating improved risk-return trade-offs over classical heuristics for constrained finance problems.',
      contributor: 'Contributor: Michael Ndai'
    }
  ];

  return (
    <div className="qke-hub-detail-container">
      {/* Banner Section */}
      <div className="qke-hub-banner">
        <div className="qke-hub-banner-bg">
          <img src={hub.bannerImage} alt={hub.name} />
          <div className="qke-hub-banner-overlay"></div>
        </div>
        <div className="qke-hub-banner-content">
          <div className="qke-hub-banner-logo-container">
            <img src={hub.logo} alt={`${hub.name} logo`} className="qke-hub-banner-logo" />
          </div>
          <h1 className="qke-hub-banner-title">{hub.name} - {hub.subtitle}</h1>
        </div>
      </div>

      {/* Hub Content */}
      <div className="qke-hub-content-section">
        <button onClick={onBack} className="qke-hub-back-link" style={{ borderColor: `${hub.color}40` }}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Back to Hubs
        </button>
        
        <div className="qke-hub-content-wrapper">
          <div className="qke-hub-description">
            <h2 style={{ borderLeftColor: hub.color }}>About This Hub</h2>
            <p className="qke-hub-lead-text">{hub.description}</p>
          </div>

          <div className="qke-hub-details">
            <h3 style={{ borderLeftColor: hub.color }}>What We Do</h3>
            <p>{hub.content}</p>
          </div>

          {/* Workshops Section - Only for QKenya Hub */}
          {hub.id === 'education' && (
            <div className="qke-workshops-section">
              <h3 style={{ borderLeftColor: hub.color }}>Workshops</h3>
              <ul className="qke-workshops-list">
                <li className="qke-workshop-item">
                  <span className="qke-workshop-badge" style={{ backgroundColor: hub.color }}>QBronze188</span>
                  <div className="qke-workshop-info">
                    <span className="qke-workshop-title">Quantum Computing and Programming Workshop</span>
                    <span className="qke-workshop-date">May 21–22 &amp; 28–29, 2026</span>
                  </div>
                </li>
              </ul>
            </div>
          )}

          {/* Explore QWorld Link - Only for QKenya Hub */}
          {hub.id === 'education' && (
            <div className="qke-explore-section">
              <a
                href="https://qworld.net/qkenya/"
                target="_blank"
                rel="noopener noreferrer"
                className="qke-explore-qworld-link"
                style={{ borderColor: hub.color }}
              >
                <span className="qke-explore-qworld-text">Explore QWorld</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a
                href="https://www.linkedin.com/showcase/qkenya"
                target="_blank"
                rel="noopener noreferrer"
                className="qke-explore-qworld-link"
                style={{ borderColor: hub.color }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                <span className="qke-explore-qworld-text">LinkedIn</span>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
          )}

          {/* Use Case Repository - Only for Research Hub */}
          {hub.id === 'research' && (
            <div className="qke-use-case-section">
              <div className="qke-use-case-header">
                <h3 style={{ borderLeftColor: hub.color }}>Our Use Case Repository</h3>
                <button
                  className="qke-cta-button"
                  style={{ backgroundColor: hub.color }}
                  onClick={() => setShareModalOpen(true)}
                >
                  Share Use Case
                </button>
              </div>
              <div className="qke-use-case-grid">
                {useCases.map((useCase, index) => (
                  <div key={index} className="qke-use-case-card">
                    <div className="qke-use-case-card-header">
                      <h4>{useCase.title}</h4>
                      <div className="qke-sdg-badge" style={{ backgroundColor: useCase.sdgColor }}>
                        <div className="qke-sdg-number">{useCase.sdg}</div>
                        <div className="qke-sdg-title">{useCase.sdgTitle}</div>
                      </div>
                    </div>
                    <p className="qke-use-case-description">{useCase.description}</p>
                    <p className="qke-use-case-contributor">{useCase.contributor}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* White Paper Section - Only for Industry Hub */}
          {hub.id === 'industry' && (
            <div className="qke-white-paper-section">
              <div className="qke-white-paper-card">
                <div className="qke-white-paper-bg">
                  <img src={whitePaperBanner} alt="QuantumKe white paper banner" />
                  <div className="qke-white-paper-overlay"></div>
                </div>
                <div className="qke-white-paper-content">
                  <div className="qke-book-icon">
                    <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                      <rect x="20" y="10" width="40" height="60" rx="2" stroke="white" strokeWidth="2" fill="none"/>
                      <rect x="23" y="13" width="34" height="54" rx="1" fill="rgba(255,255,255,0.1)"/>
                      <line x1="30" y1="25" x2="50" y2="25" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                      <line x1="30" y1="32" x2="50" y2="32" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                      <line x1="30" y1="39" x2="45" y2="39" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M20 70L20 10" stroke="white" strokeWidth="3" strokeLinecap="round"/>
                    </svg>
                    <div className="qke-white-paper-text">Kenyan Quantum White Paper</div>
                  </div>
                  
                </div>
                <div className="qke-coming-soon-badge">Coming Soon</div>
              </div>
            </div>
          )}
        </div>
      </div>
      {shareModalOpen && (
        <ShareUseCaseModal hubColor={hub.color} onClose={() => setShareModalOpen(false)} />
      )}
    </div>
  );
};

export default App;